import Button from "@/components/Button"
import  { TypographyMuted, TypographyXS } from "@/components/ui/typography"
import { Employee } from "@/types/Employee"
import { UserPermissions } from "@/types/User"
import { CircleCheckBig, OctagonX, Plus, Trash2 } from "lucide-react"
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { createUserPermissions, deleteUserPermissions } from "@/api/business/user"
import { UserPermissionsKey } from "@/queries/useUserPermissionsQuery"

interface props {
    listEmployee: Employee,
    dataUserPermissions: UserPermissions[],
    idUser: number
}

const CardEmployee = ({ listEmployee, dataUserPermissions, idUser }: props) => {
    const queryClient = useQueryClient()
    const { toast } = useToast() 

    const haveAccess = dataUserPermissions?.some(
        permission => permission.SK_FUNCIONARIO === listEmployee.SK_FUNCIONARIO
    )

    const { mutate: mudateAddPermission, isPending: mutateAddPermissionLoading } = useMutation({
        mutationFn: createUserPermissions,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UserPermissionsKey.all })
            
            toast({
                title: 'Permissão adicionada',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível adicionar a permissão. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar a permissão',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const { mutate: mudateDeletePermission, isPending: mutateDeletePermissionLoading } = useMutation({
        mutationFn: deleteUserPermissions,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UserPermissionsKey.all })
            
            toast({
                title: 'Permissão removida',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível remover a permissão. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar a permissão',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const handleAddPermission = () => {
        mudateAddPermission({
            SK_FUNCIONARIO: listEmployee.SK_FUNCIONARIO,
            SK_USUARIO: idUser
        })
    }

    const handleDeletePermission = () => {
        mudateDeletePermission({
            SK_USUARIO_X_FUNCIONARIO: dataUserPermissions.find(item => item.SK_FUNCIONARIO == listEmployee.SK_FUNCIONARIO)?.SK_USUARIO_X_FUNCIONARIO || 0
        })
    }

    return (
        
        <div className="bg-[#5a8db50d] p-2 rounded-lg border" key={listEmployee.SK_FUNCIONARIO}>   
            <div className="flex justify-between items-center">
                <TypographyMuted>
                    # {listEmployee.NK_FUNCIONARIO}
                </TypographyMuted>
                {listEmployee.DD_TIPO_VENDEDOR == 'INATIVO' && (
                    <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-300">
                        Inativo
                    </div>
                )}
            </div>
            <TypographyXS className="flex-1 text-start text-sm mb-2">
                {listEmployee.NM_FUNCIONARIO}
            </TypographyXS> 
            <TypographyMuted className="flex-1 text-start text-sm mb-2">
                {listEmployee.DD_CARGO}
            </TypographyMuted> 
            <div className="flex justify-between">
                <div className="flex items-center">
                {haveAccess ? (
                    <div className="flex gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-200 text-green-800">
                        <CircleCheckBig size={16}/>
                        Com acesso
                    </div>
                ) : (
                    <div className="flex gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                        <OctagonX size={16}/>
                        Sem acesso
                    </div>
                )} 
                </div>
                {haveAccess ? (
                    <Button className="flex gap-2 bg-red-700 hover:bg-red-900" size={"sm"} isLoading={mutateDeletePermissionLoading} onClick={() => handleDeletePermission()}>
                        <Trash2 size={16}/>
                       Remover
                    </Button>
                ) : (
                    <Button className="flex gap-2 bg-primary-400" size={"sm"} isLoading={mutateAddPermissionLoading} onClick={() => handleAddPermission()}>
                        <Plus size={16}/>
                        Adicionar
                    </Button>
                )}
            </div>
        </div> 
    )
}

export default CardEmployee
